import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { message, result, createDataItemSigner, dryrun } from "@permaweb/aoconnect";
import { AlertCircle, RefreshCw, User } from 'lucide-react';

// Constants and Utilities
import {
  AO_PROCESS_ID,
  SEND_TOKEN_PROCESS_ID,
  API_URL,
  ARWEAVE_APP_NAME,
  AMPOINTS_THRESHOLD,
  COSMIC_ROLES
} from '../utils/constants';
import { customProcess } from '../utils/aoConnection';
import { cacheManager } from '../utils/cache';
import { safeParseJson, retry, generateTimeSeriesData } from '../utils/helpers';
import {
  formatTokenAmount,
  formatNumber,
  getTitleFromPoints,
  buildKeywords,
  normalizeGuildRoles
} from '../utils/formatters';
import { generateProfilePageHTML } from '../utils/profilePageTemplate';

// UI Components
import CyberGrid from './ui/CyberGrid';
import PremiumButton from './ui/PremiumButton';

// Feature Components
import XPostsModal from './features/XPostsModal';
import PortalLoadingScreen from './features/PortalLoadingScreen';

// Layout Components
import ProfileSidebar from './layout/ProfileSideBar';
import ProfileHeader from './layout/ProfileHeader';
import ProfileContent from './layout/ProfileContent';
import ProfileFooter from './layout/ProfileFooter';

// ArNS Hook
import { useArns } from '../hooks/useArns';
import { ANT } from "@ar.io/sdk/web";
import { useProfileAutoUpdater } from "../utils/profileAutoUpdater";

// ===== TYPE DEFINITIONS =====

interface ProfileProps {
  address: string;
  walletConnected: boolean;
  linkGithub: () => void;
  linkX: () => void;
  linkDiscord: () => void;
  onDiscordIdFound?: (discordId: string) => void;
  onFetchUserError?: (error: string) => void;
}

interface StreakData {
  current: number;
  total: number;
  milestones: number[];
  history: any[];
}

interface StakingData {
  amount: number;
  stakeAt: number;
  releaseAt: number;
  lockDurationDays: number;
  remainingDays: number;
  remainingSeconds: number;
}

interface InteractionStats {
  github: { commits: number; pushes: number };
  x: { posts: number; likes_given: number };
}

interface UserData {
  processId: string;
  userId: string;
  depositAddress: string;
  derivationIndex: string;
  availableBalance: number;
  walletAddresses: string[];
  roles: string[];
  guildRoles: Record<string, any>;
  amVerifiedRoles: Record<string, any>;
  allGuildsData: Record<string, any>;
  tokenBalances: Record<string, any>;
  guildData: Record<string, any>;
  linkedAccounts: Record<string, any>;
  xId: string;
  githubId: string;
  joinDate: number;
  points: number;
  totalAiPoints: number;
  streaks: {
    sendit: StreakData;
    gm: StreakData;
    gn: StreakData;
  };
  xPostingsCount: number;
  githubInteractionsCount: number;
  interactionStats: InteractionStats;
  totalTipsSent: string;
  totalTipsReceived: string;
  ogSender: string;
  spawnGuildId: string;
  staking: StakingData;
  pointsStats: Record<string, any>;
  pointsHistory: any[];
}

interface StatusMessage {
  message: string;
  error: boolean;
}

// ===== CUSTOM HOOKS =====

const useWalletState = (walletConnected: boolean, address: string) => {
  return useMemo(() => ({
    hasWallet: walletConnected && address && window.arweaveWallet,
    walletKey: `${address}_${walletConnected}`
  }), [walletConnected, address]);
};

const useComputedValues = (userData: UserData | null) => {
  return useMemo(() => {
    if (!userData) {
      return {
        canMintPassport: false,
        pointsNeeded: AMPOINTS_THRESHOLD,
        level: 0,
        levelTitle: "Digital Wanderer",
        uniqueCosmicRoles: []
      };
    }

    const canMintPassport = userData.points >= AMPOINTS_THRESHOLD;
    const pointsNeeded = AMPOINTS_THRESHOLD - (userData.points || 0);
    const level = Math.floor(userData.points / 1000);
    const levelTitle = getTitleFromPoints(userData.points);
    const uniqueCosmicRoles = [...new Set(userData.roles?.filter(r => COSMIC_ROLES.has(r)) || [])];

    return {
      canMintPassport,
      pointsNeeded,
      level,
      levelTitle,
      uniqueCosmicRoles
    };
  }, [userData]);
};

// ===== MAIN COMPONENT =====

const Profile: React.FC<ProfileProps> = ({
  address,
  walletConnected,
  linkGithub,
  linkX,
  linkDiscord,
  onDiscordIdFound,
  onFetchUserError,
}) => {
  // ===== STATE MANAGEMENT =====
  
  // User Data State
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loadingUserData, setLoadingUserData] = useState<boolean>(false);
  const [fetchUserError, setFetchUserError] = useState<string>("");
  const [userHistory, setUserHistory] = useState<any[]>([]);
  
  // Passport State
  const [sendBalance, setSendBalance] = useState<number>(0);
  const [hasPassport, setHasPassport] = useState<boolean>(false);
  const [verifyingPassport, setVerifyingPassport] = useState<boolean>(false);
  const [passportError, setPassportError] = useState<string>("");
  
  // Avatar State (simplified)
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState<boolean>(false);
  const [avatarStatus, setAvatarStatus] = useState<StatusMessage>({ message: "", error: false });
  const [currentAvatar, setCurrentAvatar] = useState<string | null>(null);
  
  // UI State
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [copiedField, setCopiedField] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [showXPostsModal, setShowXPostsModal] = useState<boolean>(false);
  
  // ArNS Management State (for UI components that need local state)
  const [antNameInput, setAntNameInput] = useState<string>("");
  const [selectedAntProcessId, setSelectedAntProcessId] = useState<string>("");
  const [undernameInput, setUndernameInput] = useState<string>("amprofile");
  const [updatingAnt, setUpdatingAnt] = useState<boolean>(false);
  const [antUpdateStatus, setAntUpdateStatus] = useState<StatusMessage>({ message: "", error: false });
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const itemsPerPage = 10;

  // ===== CUSTOM HOOKS =====
  const { hasWallet, walletKey } = useWalletState(walletConnected, address);
  const { canMintPassport, pointsNeeded, level, levelTitle, uniqueCosmicRoles } = useComputedValues(userData);

  const {
    currentPrimaryName,
    userAntProcessId,
    antOptions,
    primaryNameRequest,
    undernames,
    loading: loadingAntCheck,
    requestingPrimaryName,
    settingUndername,
    error: antCheckError,
    fetchArnsData: checkPrimaryNameAndAnt,
    requestPrimaryName: requestPrimaryNameAction,
    setUndername,
    clearError: clearArnsError,
    lastRequestResult
  } = useArns(address, customProcess);

  const checkAndRequestWalletPermissions = useCallback(async (): Promise<boolean> => {
  try {
    // Check if wallet is available
    if (!window.arweaveWallet) {
      setAntUpdateStatus({ message: "No wallet detected. Please install ArConnect or Wander.", error: true });
      return false;
    }

    // Try to get current permissions
    try {
      await window.arweaveWallet.getActiveAddress();
      // If this works, we have basic permissions
      return true;
    } catch (error) {
      console.log("No permissions yet, requesting...");
    }

    // Request necessary permissions for ArNS operations
    const permissions = [
      'ACCESS_ADDRESS',
      'ACCESS_PUBLIC_KEY', 
      'SIGN_TRANSACTION',
      'DISPATCH'
    ];

    try {
      await window.arweaveWallet.connect(permissions);
      console.log(" Wallet permissions granted!");
      setAntUpdateStatus({ message: "Wallet permissions granted!", error: false });
      return true;
    } catch (permError) {
      console.error("Failed to get wallet permissions:", permError);
      setAntUpdateStatus({ 
        message: "Please grant wallet permissions to continue. Click your wallet extension and approve the permissions.", 
        error: true 
      });
      return false;
    }
  } catch (error: any) {
    console.error("Wallet permission check failed:", error);
    setAntUpdateStatus({ message: `Wallet error: ${error.message}`, error: true });
    return false;
  }
}, [setAntUpdateStatus]);

  // ===== COMPUTED VALUES =====
  const timeSeriesData = useMemo(() => 
    generateTimeSeriesData(userHistory, userData?.streaks, userData?.pointsHistory), 
    [userHistory, userData?.streaks, userData?.pointsHistory]
  );

  const activityStats = useMemo(() => ({
    github: userData?.interactionStats?.github || { commits: 0, pushes: 0 },
    x: userData?.interactionStats?.x || { posts: 0, likes_given: 0 }
  }), [userData?.interactionStats]);

  // ===== API FUNCTIONS =====

  const fetchUserData = useCallback(async (): Promise<void> => {
    if (!hasWallet) {
      const errorMsg = "Wallet not connected.";
      setFetchUserError(errorMsg);
      onFetchUserError?.(errorMsg);
      return;
    }

    const cacheKey = `user_data_${address}`;
    const cachedData = cacheManager.get(cacheKey);
    if (cachedData) {
      setUserData(cachedData.profile);
      setUserHistory(cachedData.history);
      return;
    }

    setLoadingUserData(true);
    setFetchUserError("");
    setUserData(null);
    setUserHistory([]);

    try {
      const signer = createDataItemSigner(window.arweaveWallet);
      const nonce = Math.random().toString(36).substring(2, 18);

      // Get user by wallet
      const userRes = await retry(() =>
        dryrun({
          process: AO_PROCESS_ID,
          tags: [
            { name: "Action", value: "Get-User-By-Wallet" },
            { name: "Wallet-Address", value: address },
            { name: "Nonce", value: nonce },
          ],
          signer,
        })
      );

      const userDataById = safeParseJson(userRes?.Messages?.[0]?.Data);
      if (!userDataById?.Success || !userDataById.userId) {
        const errorMsg = "No SendDAO account found for this wallet.";
        setFetchUserError(errorMsg);
        onFetchUserError?.(errorMsg);
        return;
      }

      const userId = userDataById.userId;
      onDiscordIdFound?.(userId);

      // Get user profile
      const tx = await message({
        process: AO_PROCESS_ID,
        tags: [
          { name: "Action", value: "Get-User-Profile" },
          { name: "User-ID", value: userId },
          { name: "Nonce", value: nonce },
        ],
        signer,
      });

      const res = await result({ message: tx, process: AO_PROCESS_ID });
      const data = res.Messages?.[0]?.Data ? JSON.parse(res.Messages[0].Data) : null;
      
      if (!data?.Success) {
        const errorMsg = "Could not retrieve profile details.";
        setFetchUserError(errorMsg);
        onFetchUserError?.(errorMsg);
        return;
      }

      const p = data.profile;
      const profile: UserData = {
        processId: p.processId || "",
        userId: p.userId || "",
        depositAddress: p.depositAddress || "Not set",
        derivationIndex: p.derivationIndex || "Not set",
        availableBalance: p.availableBalance || 0,
        walletAddresses: p.walletAddresses || [],
        roles: Array.isArray(p.roles) ? [...p.roles] : [],
        guildRoles: p.guildRoles ? normalizeGuildRoles(p.guildRoles) : {},
        amVerifiedRoles: p.amVerifiedRoles ? normalizeGuildRoles(p.amVerifiedRoles) : {},
        allGuildsData: p.allGuildsData || {},
        tokenBalances: p.tokenBalances || {},
        guildData: p.guildData || {},
        linkedAccounts: p.linkedAccounts || {},
        xId: p.xId || "Not linked",
        githubId: p.githubId || "Not linked",
        joinDate: p.joinDate || 0,
        points: p.points || 0,
        totalAiPoints: p.totalAiPoints || 0,
        streaks: p.streaks || {
          sendit: { current: 0, total: 0, milestones: [10, 25, 50, 100, 200], history: []}, 
          gm: { current: 0, total: 0, milestones: [7, 30, 60, 100, 365], history: []}, 
          gn: { current: 0, total: 0, milestones: [7, 30, 60, 100, 365], history: []} 
        },
        xPostingsCount: p.xPostingsCount || 0,
        githubInteractionsCount: p.githubInteractionsCount || 0,
        interactionStats: p.interactionStats || { github: {commits: 0, pushes: 0}, x: {posts: 0, likes_given: 0} },
        totalTipsSent: p.totalTipsSent ? formatTokenAmount(p.totalTipsSent) : "0.00",
        totalTipsReceived: p.totalTipsReceived ? formatTokenAmount(p.totalTipsReceived) : "0.00",
        ogSender: p.ogSender || "Not set",
        spawnGuildId: p.spawnGuildId || "Not set",
        staking: {
          amount: p.staking?.amount || 0,
          stakeAt: p.staking?.stakeAt || 0,
          releaseAt: p.staking?.releaseAt || 0,
          lockDurationDays: 0,
          remainingDays: 0,
          remainingSeconds: 0,
        },
        pointsStats: p.pointsStats || p.pointsBreakdown || {},
        pointsHistory: Array.isArray(p.pointsHistory) ? p.pointsHistory : [],

      };

      // Calculate staking info
      if (profile.staking.amount > 0) {
        const nowSec = Math.floor(Date.now() / 1000);
        const remainingSec = Math.max(0, profile.staking.releaseAt - nowSec);
        profile.staking.remainingSeconds = remainingSec;
        profile.staking.remainingDays = remainingSec / 86400;
        
        if (profile.staking.stakeAt > 0 && profile.staking.releaseAt > 0) {
          profile.staking.lockDurationDays = (profile.staking.releaseAt - profile.staking.stakeAt) / 86400;
        }
      }

      // Get user history
      const historyRes = await retry(() =>
        dryrun({
          process: AO_PROCESS_ID,
          tags: [
            { name: "Action", value: "Get-User-History" },
            { name: "User-ID", value: userId },
            { name: "Nonce", value: Math.random().toString(36).substring(2, 18) },
          ],
          signer,
        })
      );

      const historyData = safeParseJson(historyRes?.Messages?.[0]?.Data);
      const history = historyData?.history || [];

      // Cache the data
      const cacheData = { profile, history };
      cacheManager.set(cacheKey, cacheData);
      setUserData(profile);
      setUserHistory(history);

    } catch (err: any) {
      const errorMsg = `Failed to fetch profile: ${err.message}`;
      setFetchUserError(errorMsg);
      onFetchUserError?.(errorMsg);
    } finally {
      setLoadingUserData(false);
    }
  }, [hasWallet, address, onDiscordIdFound, onFetchUserError]);

  const verifyAmPassport = useCallback(async (): Promise<void> => {
    if (!hasWallet) {
      setPassportError("Wallet not connected.");
      return;
    }

    if (!canMintPassport) {
      setPassportError(`You need ${formatNumber(pointsNeeded)} more AmPoints to be eligible for amPassport.`);
      return;
    }

    setVerifyingPassport(true);
    setPassportError("");
    setHasPassport(false);

    try {
      const signer = createDataItemSigner(window.arweaveWallet);
      const nonce = Math.random().toString(36).substring(2, 18);
      
      const tx = await retry(() =>
        message({
          process: SEND_TOKEN_PROCESS_ID,
          tags: [
            { name: "Action", value: "Balance" },
            { name: "Target", value: address },
            { name: "Nonce", value: nonce },
          ],
          signer,
        })
      );

      const res = await retry(() => result({ message: tx, process: SEND_TOKEN_PROCESS_ID }));
      const balanceTag = res?.Messages?.[0]?.Tags?.find(t => t.name === "Balance");
      const balanceValue = balanceTag ? parseInt(balanceTag.value, 10) : 0;

      const formattedBalance = Number(formatTokenAmount(balanceValue));
      setSendBalance(formattedBalance);
      setHasPassport(balanceValue > 0);
      
      if (balanceValue <= 0) {
        setPassportError(`amPassport not found. Mint your passport to activate SEND token rewards.`);
      } else {
        setPassportError(`amPassport verified! Balance: ${formattedBalance} SEND`);
      }
    } catch (err: any) {
      setPassportError(`Passport verification failed: ${err.message}`);
    } finally {
      setVerifyingPassport(false);
    }
  }, [hasWallet, address, canMintPassport, pointsNeeded]);

  // ===== ArNS HANDLERS =====

 const requestPrimaryName = useCallback(async (): Promise<void> => {
  if (!antNameInput && !selectedAntProcessId) {
    return;
  }

  try {
    // Ensure wallet permissions before making request
    await window.arweaveWallet.connect([
      'ACCESS_ADDRESS',
      'ACCESS_PUBLIC_KEY',
      'SIGN_TRANSACTION',
      'DISPATCH'
    ]);

    await requestPrimaryNameAction({
      antName: antNameInput,
      selectedAntProcessId: selectedAntProcessId
    });

    // Clear form after request
    setAntNameInput("");
    setSelectedAntProcessId("");
  } catch (error: any) {
    console.error("Failed to request primary name:", error);
    setAntUpdateStatus({ 
      message: `Error: ${error.message || "Failed to request primary name"}`, 
      error: true 
    });
  }
}, [antNameInput, selectedAntProcessId, requestPrimaryNameAction]);

  const handleUpdateAntMetadata = useCallback(async (): Promise<void> => {
    if (!hasWallet || !userData || !userAntProcessId) {
      setAntUpdateStatus({ message: "Cannot update: Missing requirements.", error: true });
      return;
    }

    setUpdatingAnt(true);
    setAntUpdateStatus({ message: "Updating metadata...", error: false });

    try {
      const signer = createDataItemSigner(window.arweaveWallet);
      const ant = ANT.init({ signer, processId: userAntProcessId, process: customProcess });

      const owner = await retry(() => ant.getOwner());
      if (owner !== address) {
        throw new Error("Not the owner of this ANT.");
      }

      const description = "amPassport User Profile";
      await retry(() => ant.setDescription({ description }));

      const keywords = buildKeywords(userData);
      if (keywords.length) {
        await retry(() => ant.setKeywords({ keywords }));
      }

      setAntUpdateStatus({ message: "Metadata updated successfully!", error: false });
      cacheManager.cclear();
      
    } catch (err: any) {
      setAntUpdateStatus({ message: `Error: ${err.message || "Unknown error"}`, error: true });
    } finally {
      setUpdatingAnt(false);
    }
  }, [hasWallet, userData, userAntProcessId, address]);

  const handleSetUndername = useCallback(async (): Promise<void> => {
  if (!undernameInput || !userData) {
    console.error("Missing required data:", { undernameInput, userData });
    return;
  }

  // Check wallet permissions first
  const hasPermissions = await checkAndRequestWalletPermissions();
  if (!hasPermissions) {
    console.error("Cannot proceed without wallet permissions");
    return;
  }

  // Update status to show we're starting
  setUpdatingAnt(true);
  setAntUpdateStatus({ message: "Processing...", error: false });

  console.log("Starting undername setup for:", undernameInput);

  // EARLY OWNERSHIP CHECK - Before doing any expensive operations
  if (userAntProcessId) {
    try {
      // Skip ownership verification - the getOwner() method returns incorrect data
      // The ANT process will verify ownership when we try to write
      console.log("Proceeding with ANT process:", userAntProcessId);
    } catch (error: any) {
      console.error("Error with ANT process:", error);
      setAntUpdateStatus({ 
        message: `Error: ${error.message}`, 
        error: true 
      });
      setUpdatingAnt(false);
      return;
    }
  }

  if (undernameInput === "amprofile") {
    try {
      // Step 1: Generate profile HTML
      console.log("Step 1: Generating profile HTML...");
      setAntUpdateStatus({ message: "Generating profile page...", error: false });
      
            // When calling generateProfilePageHTML, use real data:
      const profileHTML = generateProfilePageHTML({
        userId: userData.userId,
        processId: userData.processId,
        points: userData.points,
        currentPrimaryName: currentPrimaryName || undefined,
        avatarUrl: currentAvatar || undefined,
        xId: userData.xId !== "Not linked" ? userData.xId : undefined,
        githubId: userData.githubId !== "Not linked" ? userData.githubId : undefined,
        levelTitle: levelTitle,
        level: level,
        pointsBreakdown: userData.pointsStats,
        xPostingsCount: userData.xPostingsCount, 
        joinDate: userData.joinDate, 
        githubInteractionsCount: userData.githubInteractionsCount
      });

      // Step 2: Upload to Arweave
      console.log("Step 2: Uploading to Arweave...");
      setAntUpdateStatus({ message: "Uploading profile to Arweave...", error: false });
      
      const signedItem = await window.arweaveWallet.signDataItem({
        data: new TextEncoder().encode(profileHTML),
        tags: [
          { name: "Content-Type", value: "text/html" },
          { name: "App-Name", value: ARWEAVE_APP_NAME },
          { name: "Type", value: "ampassport-profile" },
          { name: "User-ID", value: userData.userId },
          { name: "Process-ID", value: userData.processId }
        ],
      });

      const uploadRes = await fetch("https://up.arweave.net/tx", {
        method: "POST",
        headers: { "Content-Type": "application/octet-stream" },
        body: signedItem,
      });

      if (!uploadRes.ok) {
        throw new Error(`Upload failed: ${uploadRes.statusText}`);
      }

      const { id: txId } = await uploadRes.json();
      console.log("Upload successful! Transaction ID:", txId);
      console.log("View at: https://arweave.net/" + txId);

      // Step 3: Wait for transaction to propagate
      console.log("Step 3: Waiting for transaction to propagate...");
      setAntUpdateStatus({ message: "Waiting for transaction confirmation...", error: false });
      await new Promise(resolve => setTimeout(resolve, 10000)); // 10 seconds wait

      // Step 4: Set the undername
      console.log("Step 4: Setting undername with transaction ID:", txId);
      setAntUpdateStatus({ message: "Setting ampassport undername...", error: false });
      
      try {
        // Import ArConnectSigner from the SDK - EXACTLY as shown in docs
        const { ANT, ArconnectSigner } = await import("@ar.io/sdk/web");
        
        // Initialize ANT with ArConnectSigner - NO customProcess!
        console.log("Initializing ANT with process ID:", userAntProcessId);
        const ant = ANT.init({ 
          signer: new ArconnectSigner(window.arweaveWallet),
          processId: userAntProcessId
          // DO NOT pass process parameter - this is the issue!
        });

        // Set the undername record
        console.log("Setting undername record...");
        const { id: messageId } = await ant.setUndernameRecord({
          undername: undernameInput,
          transactionId: txId,
          ttlSeconds: 900
        });
        
        console.log("Undername set successfully! Message ID:", messageId);

        // Step 5: Verify it was set
        console.log("Step 5: Verifying undername was set...");
        await new Promise(resolve => setTimeout(resolve, 5000)); 
        
        // Refresh the ArNS data to see the new undername
        await checkPrimaryNameAndAnt(true);
        
        setAntUpdateStatus({ 
          message: `Success! Your profile will be available at https://amprofile_${currentPrimaryName}.ar.io`, 
          error: false 
        });
        
        console.log(" amPassport setup complete!");
        console.log(`Profile page: https://arweave.net/${txId}`);
        console.log(`ArNS URL: https://amprofile_${currentPrimaryName}.ar.io`);

      } catch (undernameError: any) {
        console.error("Failed to set undername:", undernameError);
        throw new Error(`Failed to set undername: ${undernameError.message}`);
      }

    } catch (error: any) {
      console.error("Failed to create ampassport profile:", error);
      setAntUpdateStatus({ 
        message: `Error: ${error.message || "Failed to set amprofile"}`, 
        error: true 
      });
    } finally {
      setUpdatingAnt(false);
    }
  } else {
    // For other undernames, use the default AO link
    try {
      const result = await setUndername({
        undername: undernameInput,
        url: `https://ao.link/entity/${userData?.processId}`
      });
      
      if (lastRequestResult?.success === false) {
        throw new Error(lastRequestResult.message || "Failed to set undername");
      }
      
      setAntUpdateStatus({ 
        message: `Undername "${undernameInput}" set successfully!`, 
        error: false 
      });
      
      // Refresh data
      await checkPrimaryNameAndAnt(true);
      
    } catch (error: any) {
      setAntUpdateStatus({ 
        message: `Error: ${error.message}`, 
        error: true 
      });
    } finally {
      setUpdatingAnt(false);
    }
  }
}, [
  setUndername, 
  undernameInput, 
  userData, 
  currentPrimaryName, 
  currentAvatar, 
  levelTitle, 
  level, 
  userAntProcessId,
  checkPrimaryNameAndAnt,
  lastRequestResult,
  setUpdatingAnt,
  setAntUpdateStatus,
  checkAndRequestWalletPermissions
]);

  const handleAvatarChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setAvatarFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);
  }, []);

  const handleUploadAvatar = useCallback(async (): Promise<void> => {
    if (!hasWallet || !userData || !canMintPassport || !userAntProcessId || !avatarFile) {
      setAvatarStatus({ message: "Cannot upload avatar: Missing requirements or no file selected.", error: true });
      return;
    }

    setUploadingAvatar(true);
    setAvatarStatus({ message: "Uploading avatar...", error: false });

    try {
      const signer = createDataItemSigner(window.arweaveWallet);
      const ant = ANT.init({ signer, processId: userAntProcessId, process: customProcess });

      const owner = await retry(() => ant.getOwner());
      if (owner !== address) {
        throw new Error("Not the owner of this ANT.");
      }

      const fileReader = new FileReader();
      fileReader.readAsArrayBuffer(avatarFile);
      const fileData = await new Promise<ArrayBuffer>((resolve, reject) => {
        fileReader.onload = () => resolve(fileReader.result as ArrayBuffer);
        fileReader.onerror = reject;
      });

      const signedItem = await window.arweaveWallet.signDataItem({
        data: new Uint8Array(fileData), 
        tags: [
          { name: "Content-Type", value: avatarFile.type },
          { name: "App-Name", value: ARWEAVE_APP_NAME },
          { name: "Type", value: "avatar" },
        ],
      });

      const uploadRes = await fetch("https://up.arweave.net/tx", {  
        method: "POST",
        headers: { "Content-Type": "application/octet-stream" },
        body: signedItem,
      });
      
      if (!uploadRes.ok) {
        throw new Error(`Upload failed: ${uploadRes.statusText}`);
      }
      
      const { id: txId } = await uploadRes.json();

      // Use the new setUndernameRecord method instead of deprecated setRecord
      await retry(() =>
        ant.setUndernameRecord({
          undername: "avatar",
          transactionId: txId,
          ttlSeconds: 60 * 60 * 24 * 7,
        })
      );

      setAvatarStatus({ message: "Avatar uploaded and set successfully!", error: false });
      setCurrentAvatar(`https://arweave.net/${txId}`);
      setAvatarFile(null);
      setAvatarPreview(null);
    } catch (err: any) {
      setAvatarStatus({ message: `Error: ${err.message || "Unknown error"}`, error: true });
    } finally {
      setUploadingAvatar(false);
    }
  }, [hasWallet, userData, canMintPassport, userAntProcessId, address, avatarFile]);

  const syncAntData = useCallback(async (): Promise<void> => {
    if (!hasWallet || !userData || !currentPrimaryName || !userAntProcessId) {
      setAntUpdateStatus({ message: "Cannot sync: Missing data.", error: true });
      return;
    }

    setUpdatingAnt(true);
    setAntUpdateStatus({ message: "Syncing data...", error: false });

    try {
      await checkPrimaryNameAndAnt(true);

      const res = await retry(() =>
        fetch(`${API_URL}/api/update-ant`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            discordId: userData.userId,
            walletAddress: address,
            antName: currentPrimaryName,
            antProcessId: userAntProcessId,
            processId: userData.processId,
            amPoints: userData.points,
          })
        }).then(response => response.json())
      );

      if (!res.success) {
        throw new Error(res.message || "Backend sync failed.");
      }

      setAntUpdateStatus({ message: "Data synced successfully!", error: false });
      cacheManager.cclear();
      
    } catch (err: any) {
      setAntUpdateStatus({ message: `Sync error: ${err.message || "Unknown error"}`, error: true });
    } finally {
      setUpdatingAnt(false);
    }
  }, [hasWallet, userData, address, currentPrimaryName, userAntProcessId, checkPrimaryNameAndAnt]);

  const { checking, updating, checkAndUpdate } = useProfileAutoUpdater({
  userData,
  userAntProcessId,
  currentPrimaryName,
  currentAvatar,
  levelTitle,
  level,
  enabled: true 
});

  // ===== EFFECTS =====

  useEffect(() => {
    if (walletConnected && address) {
      fetchUserData();
    } else {
      // Reset all state when wallet disconnected
      setUserData(null);
      setFetchUserError("");
      setSendBalance(0);
      setHasPassport(false);
      setCurrentAvatar(null);
      setUserHistory([]);
      cacheManager.cclear();
    }
  }, [walletKey, fetchUserData]);

  // Update current avatar when ANT data changes
  useEffect(() => {
    if (userAntProcessId && currentPrimaryName) {
      const avatarRecord = undernames?.avatar;
      if (avatarRecord?.transactionId) {
        setCurrentAvatar(`https://arweave.net/${avatarRecord.transactionId}`);
      }
    }
  }, [userAntProcessId, currentPrimaryName, undernames]);

  // ===== RENDER HELPERS =====

  if (loadingUserData) {
    return <PortalLoadingScreen />;
  }

  if (fetchUserError) {
    return (
      <div className="min-h-screen bg-[#0A0E1B] flex items-center justify-center">
        <CyberGrid />
        <motion.div 
          className="relative z-10 text-center max-w-md"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div 
            className="w-20 h-20 bg-red-500/20 rounded-3xl flex items-center justify-center mx-auto mb-6"
            animate={{ 
              boxShadow: [
                "0 0 20px rgba(255,71,71,0.3)",
                "0 0 40px rgba(255,71,71,0.5)",
                "0 0 20px rgba(255,71,71,0.3)"
              ]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <AlertCircle className="w-10 h-10 text-red-400" />
          </motion.div>
          <div className="text-2xl font-semibold text-[#E8FDF2] mb-3">Connection Error</div>
          <div className="text-red-400 mb-6">{fetchUserError}</div>
          <PremiumButton onClick={fetchUserData} disabled={false} icon={<RefreshCw className="w-5 h-5" />}>
            Try Again
          </PremiumButton>
        </motion.div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-[#0A0E1B] flex items-center justify-center">
        <CyberGrid />
        <motion.div 
          className="relative z-10 text-center max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div 
            className="w-20 h-20 bg-[#8AA5A2]/20 rounded-3xl flex items-center justify-center mx-auto mb-6"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
          >
            <User className="w-10 h-10 text-[#8AA5A2]" />
          </motion.div>
          <div className="text-2xl font-semibold text-[#E8FDF2] mb-3">No Profile Found</div>
          <div className="text-[#8AA5A2] mb-6">Connect your wallet to view your amPassport profile.</div>
          <PremiumButton onClick={fetchUserData} disabled={false} icon={<RefreshCw className="w-5 h-5" />}>
            Refresh
          </PremiumButton>
        </motion.div>
      </div>
    );
  }

  // ===== MAIN RENDER =====

  return (
    <div className="min-h-screen bg-[#0A0E1B] text-white flex flex-col">
      <CyberGrid />
      
      {/* X Posts Modal */}
      <AnimatePresence>
        {showXPostsModal && (
          <XPostsModal
            isOpen={showXPostsModal}
            onClose={() => setShowXPostsModal(false)}
            userData={userData}
            processId={AO_PROCESS_ID}
          />
        )}
      </AnimatePresence>
      
      {/* Main Container */}
      <div className="relative z-10 flex-grow flex justify-center w-full py-16">
        <div className="w-full max-w-[1536px] px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col xl:flex-row-reverse gap-8">
            
            {/* Sidebar */}
            <motion.div 
              className="xl:w-80 xl:flex-shrink-0"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
            >
              <ProfileSidebar 
                userData={userData}
                currentAvatar={currentAvatar}
                level={level}
                levelTitle={levelTitle}
                currentPrimaryName={currentPrimaryName}
                address={address}
                copiedField={copiedField}
                setCopiedField={setCopiedField}
              />
            </motion.div>

            {/* Content Area */}
            <motion.div 
              className="flex-grow overflow-y-auto"
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1], delay: 0.2 }}
            >
              <div className="pb-8">
                {/* Navigation Header */}
                <ProfileHeader 
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  fetchUserData={fetchUserData}
                />
                
                {/* Tab Content */}
                <ProfileContent 
                  activeTab={activeTab}
                  userData={userData}
                  currentAvatar={currentAvatar}
                  timeSeriesData={timeSeriesData}
                  githubActivityTypes={activityStats.github}
                  xActivityTypes={activityStats.x}
                  setShowXPostsModal={setShowXPostsModal}
                  canMintPassport={canMintPassport}
                  pointsNeeded={pointsNeeded}
                  verifyAmPassport={verifyAmPassport}
                  verifyingPassport={verifyingPassport}
                  passportError={passportError}
                  linkX={linkX}
                  linkGithub={linkGithub}
                  linkDiscord={linkDiscord}
                  address={address}
                  copiedField={copiedField}
                  setCopiedField={setCopiedField}
                  // ArNS props from hook
                  currentPrimaryName={currentPrimaryName}
                  userAntProcessId={userAntProcessId}
                  primaryNameRequest={primaryNameRequest}
                  antOptions={antOptions}
                  loadingAntCheck={loadingAntCheck}
                  antCheckError={antCheckError}
                  checkPrimaryNameAndAnt={checkPrimaryNameAndAnt}
                  antNameInput={antNameInput}
                  setAntNameInput={setAntNameInput}
                  selectedAntProcessId={selectedAntProcessId}
                  setSelectedAntProcessId={setSelectedAntProcessId}
                  requestPrimaryName={requestPrimaryName}
                  handleUpdateAntMetadata={handleUpdateAntMetadata}
                  updatingAnt={updatingAnt}
                  antUpdateStatus={antUpdateStatus}
                  undernameInput={undernameInput}
                  setUndernameInput={setUndernameInput}
                  handleSetUndername={handleSetUndername}
                  settingUndername={settingUndername}
                  undernameStatus={{ message: lastRequestResult?.message || "", error: !lastRequestResult?.success }}
                  avatarFile={avatarFile}
                  avatarPreview={avatarPreview}
                  handleAvatarChange={handleAvatarChange}
                  uploadingAvatar={uploadingAvatar}
                  handleUploadAvatar={handleUploadAvatar}
                  avatarStatus={avatarStatus}
                  undernames={undernames}
                  syncAntData={syncAntData}
                  userHistory={userHistory}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  itemsPerPage={itemsPerPage}
                  level={level}
                  levelTitle={levelTitle}
                  setUpdatingAnt={setUpdatingAnt}
                  setAntUpdateStatus={setAntUpdateStatus}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="w-full bg-[#0A0E1B]">
        <div className="max-w-[1536px] mx-auto px-4 sm:px-6 lg:px-8">
          <ProfileFooter />
        </div>
      </div>
    </div>
  );
};

export default Profile;